/**
 * Overview: how the selected stack, or the selected group of stacks, is doing
 * in the time window. One strip of the figures a person comes for, daily
 * spend beside where the money goes, the detail as a list of facts, then the
 * largest stacks (a group) or the stack's facts (one stack).
 *
 * Every number comes from selectors.ts with the period it covers. A number
 * that follows the time window has no tag. Any other number says its period.
 */
import { useEffect, useMemo } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { Badge, Button, Table } from 'react-bootstrap';
import { Doughnut, Line } from 'react-chartjs-2';
import type { ChartData, ChartOptions } from 'chart.js';
import '../chartSetup';
import { ND_CHART_PAL, ND_COLORS, ND_FILL } from '@/styles/theme';
import { timeAgo } from '@/utils/relativeTime';
import type { DashboardView } from '@/types/fleetAnalytics';
import { useCurrency } from '../currencyContext';
import type { FleetData } from '../fleetData';
import {
  isReady,
  needsOf,
  selectActiveUsers,
  selectApps,
  selectChat,
  selectCounts,
  selectCredits,
  selectPeakDailyUsers,
  selectScheduled,
  selectSeries,
  selectServiceMix,
  selectSpend,
  selectStackRows,
  selectTimeSaved,
  selectTrailingAverage,
} from '../selectors';
import type { ServiceMix, StackRow, StackRows } from '../selectors';
import { fmtN, fmtUSD, fmtUSD0, fmtUSDc, pct } from '../shared';
import type { WindowState } from '../shared';
import { Empty, Fact, FactGroup, Facts, Kpi, KpiGrid, NeedsTier, Panel } from '../ui/kit';
import {
  dayLabel,
  fmtCredits,
  fmtMinutes,
  inactiveSpendWords,
  LinkButton,
  moneyScale,
  notBilledWords,
  plural,
  rowPool,
  settled,
  subLines,
  tile,
  Waiting,
  waitingTile,
  windowInWords,
} from './common';
import type { DashboardTabProps } from './registry';
import './Overview.css';

const SPEND_AVERAGE_DAYS = 7;
const LARGEST_STACKS = 10;
const SERVICE_SLICES = 6;

const TOTAL_SPEND_HINT =
  'The AWS bill, with model usage that ran on a shared account moved to the stack that used it. Model usage is priced by Numa from token counts.';

const NOT_BILLED_HINT =
  'Credits used this month past the allocation by clients Arcanum does not bill for credits: legacy, own account, trial and internal stacks, and partners not billed as Credits. Nobody owes these.';

/** "Counted over the 42 clients on credits only." The credits owed and prepaid count no other client. */
function clientsOnCredits(n: number | null): string {
  if (n === null) return 'Counted over the clients on credits only.';
  return `Counted over the ${plural(n, 'client')} on credits only.`;
}

const MONTH_LONG = new Intl.DateTimeFormat('en-NZ', { month: 'long', year: 'numeric', timeZone: 'UTC' });
/** A billing month, `YYYY-MM`, in words: "October 2026". */
const monthLabel = (month: string) => MONTH_LONG.format(new Date(`${month}-01T00:00:00Z`));

export function Overview({
  data,
  window: win,
  snapshots,
  fleet,
  selection,
  onSelectClient,
  onSelectTab,
}: DashboardTabProps) {
  // The money formats read the chosen currency. This redraws the tab when it changes.
  const currency = useCurrency();

  const spend = useMemo(() => selectSpend(data, win), [data, win]);
  const chat = useMemo(() => selectChat(data, win), [data, win]);
  const activePeople = useMemo(() => selectActiveUsers(data, win), [data, win]);
  const busiestDay = useMemo(() => selectPeakDailyUsers(data, win), [data, win]);
  const credits = useMemo(() => selectCredits(data, win, selection.contract), [data, win, selection.contract]);
  const timeSaved = useMemo(() => selectTimeSaved(data, win), [data, win]);
  const scheduled = useMemo(() => selectScheduled(data, win), [data, win]);
  const apps = useMemo(() => selectApps(data, win), [data, win]);

  const isView = selection.kind === 'view';
  // A view: one row for each AWS account, so an account that several stacks share is listed once with its bill.
  // One stack: its own row, with the stacks it shares an account with.
  const stackRows = useMemo(() => {
    const rules = { contractOf: selection.contractOf, inactive: selection.inactive };
    return isView
      ? selectStackRows(selection.stacks, win, fleet.freshness, { onePerAccount: true, ...rules })
      : selectStackRows(rowPool(selection, snapshots), win, fleet.freshness, rules);
  }, [isView, selection, snapshots, win, fleet.freshness]);
  const ownRow = isView ? undefined : stackRows.rows.find((r) => r.client === selection.id);

  // The apps row shows only when the stack has apps and a run history to count from.
  const appsTile =
    apps && apps.hasHistory && apps.window && apps.appsRun && apps.inventory.total > 0
      ? { appsRun: apps.appsRun, runs: apps.window.runs.value }
      : null;

  // Ask for the heavier data the figures and the list wait on. The tab draws without it.
  const waiting = [spend, activePeople, appsTile?.appsRun, isView ? stackRows : null];
  const wanted = needsOf(...waiting).join(',');
  const { ensureTier } = fleet;
  useEffect(() => {
    for (const tier of needsOf(...waiting)) ensureTier(tier);
    // `wanted` names the tiers: the answers themselves are new objects on every draw.
  }, [wanted, ensureTier]);

  const sharedWith = ownRow?.sharesAccountWith ?? [];
  const failureRate = scheduled.failureRate.value;

  return (
    <div className="dash-ov">
      <KpiGrid cols={credits ? 5 : 4}>
        <Kpi
          lead
          label="Total spend"
          {...waitingTile(fleet, isReady(spend) ? settled(spend.inferred, spend) : spend, fmtUSD, TOTAL_SPEND_HINT)}
          sub={subLines(
            isReady(spend) && notBilledWords(spend),
            isReady(spend) && inactiveSpendWords(spend.inactive, 'spend'),
            sharedWith.length > 0 &&
              `Holds the whole bill of an AWS account shared with ${plural(sharedWith.length, 'other stack')}`
          )}
        />
        <Kpi
          label="Model spend from chat and agents"
          {...tile(
            chat.modelSpend,
            fmtUSD,
            'Model usage by chat, agents and scheduled runs, priced by Numa from token counts.'
          )}
          sub={isReady(spend) ? inactiveSpendWords(spend.inactive, 'chat') : undefined}
        />
        <Kpi
          label="Conversations"
          {...tile(chat.conversations, fmtN, 'Conversations started in the window.')}
          sub={`${fmtN(chat.adhocConversations.value)} by people, ${fmtN(chat.scheduledConversations.value)} by schedules`}
        />
        <Kpi
          label="Active people"
          {...waitingTile(fleet, activePeople, fmtN, 'People who sent a message in the window, each counted once.')}
          sub={`Busiest day: ${fmtN(busiestDay.value)}`}
        />
        {credits && (
          <Kpi
            label="Credits in the window"
            {...tile(credits.window, fmtCredits)}
            sub={`${fmtUSD(credits.windowUsd.value)} at the credit price`}
          />
        )}
      </KpiGrid>

      <div className="dash-cols dash-cols--2-1">
        <Panel title="Daily spend" subtitle={`Total spend by day, with its ${SPEND_AVERAGE_DAYS}-day average.`}>
          <DailySpend data={data} win={win} fleet={fleet} currencyKey={`${currency.code}:${currency.rate}`} />
        </Panel>
        <WhereTheMoneyGoes data={data} win={win} fleet={fleet} isView={isView} />
      </div>

      <Panel title="The detail" subtitle={windowInWords(win)}>
        <Facts cols={3}>
          <FactGroup title="Unit costs">
            <Fact
              label="Cost per conversation"
              {...tile(
                chat.costPerConversation,
                fmtUSD,
                'Model spend divided by the conversations started in the window.'
              )}
            />
            <Fact
              label="Cost per message"
              {...tile(chat.costPerMessage, fmtUSDc, 'Model spend divided by the messages people sent in the window.')}
            />
          </FactGroup>

          <FactGroup title="Usage">
            <Fact label="Messages" {...tile(chat.messages, fmtN, 'Messages people sent.')} />
            <Fact label="Tool calls" {...tile(chat.toolCalls, fmtN, 'Tools Numa used to do the work.')} />
            <Fact
              label="Scheduled runs"
              {...tile(scheduled.runs, fmtN, 'Scheduled and triggered runs that finished in the window.')}
            />
            <Fact
              label="Failed runs"
              tone={scheduled.failed.value > 0 ? 'bad' : 'default'}
              {...tile(
                scheduled.failed,
                fmtN,
                failureRate === null ? undefined : `${pct(failureRate, 1)} of the runs in the same period.`
              )}
            />
          </FactGroup>

          <FactGroup title={credits ? 'Credits and value' : 'Value'}>
            {credits && (
              <Fact
                label="Credits used"
                {...tile(
                  credits.month,
                  fmtCredits,
                  credits.allocation
                    ? `${monthLabel(credits.monthLabel)}, of ${fmtCredits(credits.allocation.value)} allocated.`
                    : `${monthLabel(credits.monthLabel)}.`
                )}
              />
            )}
            {credits?.balance && credits.collectible && (
              <Fact
                label={credits.balance.value < 0 ? 'Credits owed' : 'Credit balance'}
                tone={credits.balance.value < 0 ? 'bad' : 'default'}
                {...tile(
                  credits.balance,
                  (n: number) => fmtCredits(Math.abs(n)),
                  'The top-up balance. Below zero means the client owes credits.'
                )}
              />
            )}
            {/* A client Arcanum does not invoice for credits never owes. Its balance shows only above zero. */}
            {credits?.balance && credits.collectible === false && credits.balance.value > 0 && (
              <Fact
                label="Credit balance"
                {...tile(credits.balance, fmtCredits, 'Credit the client holds from top-ups.')}
              />
            )}
            {credits?.collectible === false && credits.notBilledOver && (
              <Fact
                label="Past allocation, not billed"
                {...tile(
                  credits.notBilledOver,
                  fmtCredits,
                  `Credits used this month past the allocation. ${credits.notBilledReason}`
                )}
              />
            )}
            {credits?.owed && credits.owed.value > 0 && (
              <Fact
                label="Owed by clients on credits"
                tone="warn"
                {...tile(
                  credits.owed,
                  fmtCredits,
                  `Credits used past what was paid for, by the clients on credits that owe. ${clientsOnCredits(credits.collectibleStacks)}`
                )}
              />
            )}
            {credits?.prepaid && credits.prepaid.value > 0 && (
              <Fact
                label="Prepaid by clients on credits"
                {...tile(
                  credits.prepaid,
                  fmtCredits,
                  `Credits that clients on credits paid for and did not use yet. ${clientsOnCredits(credits.collectibleStacks)}`
                )}
              />
            )}
            {credits?.collectible === null && credits.notBilledOver && credits.notBilledOver.value > 0 && (
              <Fact label="Past allocation, not billed" {...tile(credits.notBilledOver, fmtCredits, NOT_BILLED_HINT)} />
            )}
            <Fact label="Time saved" tone={timeSaved.value > 0 ? 'good' : 'default'} {...tile(timeSaved, fmtMinutes)} />
            {appsTile && (
              <Fact
                label="Apps run"
                {...waitingTile(
                  fleet,
                  appsTile.appsRun,
                  fmtN,
                  appsTile.runs === null
                    ? 'Apps and dashboards with one run or more in the window.'
                    : `Apps and dashboards with one run or more in the window: ${plural(appsTile.runs, 'run')}.`
                )}
              />
            )}
          </FactGroup>
        </Facts>
      </Panel>

      {isView ? (
        <LargestStacks
          stackRows={stackRows}
          spendFailed={fleet.tiers.services === 'error'}
          peopleFailed={fleet.tiers.users === 'error'}
          onSelectClient={onSelectClient}
          onSelectTab={onSelectTab}
        />
      ) : (
        ownRow && <AboutStack row={ownRow} data={data} />
      )}
    </div>
  );
}

// ─── daily spend ─────────────────────────────────────────────────────────

interface ChartProps {
  data: DashboardView;
  win: WindowState;
  fleet: FleetData;
}

function DailySpend({ data, win, fleet, currencyKey }: ChartProps & { currencyKey: string }) {
  const daily = useMemo(() => selectSeries(data, win, 'spend'), [data, win]);
  const average = useMemo(() => selectTrailingAverage(data, win, 'spend', SPEND_AVERAGE_DAYS), [data, win]);

  const chart = useMemo(() => {
    if (!isReady(daily) || !isReady(average)) return null;
    // Both lines stop at the last day the data runs to, so neither falls to zero on a day with no data yet.
    const chartData: ChartData<'line'> = {
      labels: daily.days.map(dayLabel),
      datasets: [
        {
          label: 'Total spend',
          data: daily.values,
          borderColor: ND_FILL.accentStrong,
          backgroundColor: ND_FILL.accent,
          borderWidth: 1.5,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.25,
          fill: true,
        },
        {
          label: `${SPEND_AVERAGE_DAYS}-day average`,
          data: average.values,
          borderColor: ND_COLORS.accent,
          backgroundColor: 'transparent',
          borderWidth: 2.5,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.3,
        },
      ],
    };
    const options: ChartOptions<'line'> = {
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'top', align: 'end', labels: { boxWidth: 12, boxHeight: 2 } },
        tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${fmtUSD(c.parsed.y)}` } },
      },
      scales: {
        x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 8 } },
        y: moneyScale(),
      },
    };
    return { chartData, options };
    // The axis and the hover text are money: draw again when the currency changes.
  }, [daily, average, currencyKey]);

  if (!chart) {
    return (
      <NeedsTier fleet={fleet} tiers={['services']} what="Spend by day">
        <Empty>Spend by day is not ready yet.</Empty>
      </NeedsTier>
    );
  }
  if (isReady(daily) && daily.days.length === 0) return <Empty>The window has no days in it yet.</Empty>;
  return (
    <div className="dash-chart">
      <Line data={chart.chartData} options={chart.options} />
    </div>
  );
}

// ─── where the money goes ────────────────────────────────────────────────

function WhereTheMoneyGoes({ data, win, fleet, isView }: ChartProps & { isView: boolean }) {
  useCurrency();
  const mix = useMemo(() => selectServiceMix(data, win), [data, win]);
  const spend = useMemo(() => selectSpend(data, win), [data, win]);
  const ready = isReady(mix) ? mix : null;
  // A standalone customer runs its own systems in the same AWS account. That spend is not Numa's.
  const notNuma = isReady(spend) ? spend.notNuma : null;
  const whose = isView ? "customers' own systems" : "the customer's own systems";

  return (
    <Panel
      title="Where the money goes"
      scope={ready?.scope}
      subtitle={
        notNuma && notNuma.value > 0
          ? `Total spend by AWS service. Leaves out ${fmtUSD(notNuma.value)} spent on ${whose}${notNuma.scope === 'all-time' ? ', all time' : ''}.`
          : 'Total spend by AWS service.'
      }
    >
      <NeedsTier fleet={fleet} tiers={['services']} what="Spend by service">
        {ready && ready.rows.length > 0 ? <ServiceDonut mix={ready} /> : <Empty>No spend in this window.</Empty>}
      </NeedsTier>
    </Panel>
  );
}

function ServiceDonut({ mix }: { mix: ServiceMix }) {
  // The largest services each get a slice. The rest share one, so the ring stays readable.
  const slices = useMemo(() => {
    const top = mix.rows
      .slice(0, SERVICE_SLICES)
      .map((r) => ({ label: r.service, usd: r.usd, share: r.share, rest: false }));
    const rest = mix.rows.slice(SERVICE_SLICES);
    if (rest.length) {
      top.push({
        label: plural(rest.length, 'other service'),
        usd: rest.reduce((a, r) => a + r.usd, 0),
        share: rest.reduce((a, r) => a + r.share, 0),
        rest: true,
      });
    }
    return top;
  }, [mix]);
  // The slice that holds every small service is grey: it is not one thing, so it gets no colour of its own.
  const colours = slices.map((s, i) => (s.rest ? ND_COLORS.neutral : ND_CHART_PAL[i % ND_CHART_PAL.length]));

  const chartData: ChartData<'doughnut'> = {
    labels: slices.map((s) => s.label),
    datasets: [
      {
        data: slices.map((s) => s.usd),
        backgroundColor: colours,
        borderColor: ND_COLORS.panel,
        borderWidth: 2,
      },
    ],
  };
  const options: ChartOptions<'doughnut'> = {
    maintainAspectRatio: false,
    cutout: '64%',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { label: (c) => `${c.label}: ${fmtUSD(c.parsed)} (${pct(slices[c.dataIndex]?.share, 1)})` },
      },
    },
  };

  return (
    <>
      <div className="dash-ov__donut">
        <Doughnut data={chartData} options={options} />
      </div>
      <ul className="dash-ov-legend">
        {slices.map((s, i) => (
          <li key={s.label} className="dash-ov-legend__row">
            <svg className="dash-ov-legend__dot" viewBox="0 0 8 8" aria-hidden="true">
              <circle cx="4" cy="4" r="4" fill={colours[i]} />
            </svg>
            <span className="dash-ov-legend__name" title={s.label}>
              {s.label}
            </span>
            <span className="dash-ov-legend__share numeric">{pct(s.share, 1)}</span>
            <span className="dash-ov-legend__usd numeric">{fmtUSD0(s.usd)}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

// ─── a view: the largest stacks ──────────────────────────────────────────

/** Why a conversation count carries a "~" on a row whose nightly record is of the older kind. */
const COUNTED_PER_DAY =
  "This stack's data counts a conversation once for each day it was active, so the count reads a little high.";

/** How many of a shared account's stacks are named on its row before "and N more". */
const MEMBERS_NAMED = 4;

interface LargestStacksProps {
  /** One row for each AWS account: stacks that share an account are one row, with the account's bill counted once. */
  stackRows: StackRows;
  /** Total spend did not load, so the list cannot be put in order of it. */
  spendFailed: boolean;
  peopleFailed: boolean;
  onSelectClient: (id: string | null) => void;
  onSelectTab?: (key: string) => void;
}

function LargestStacks({ stackRows, spendFailed, peopleFailed, onSelectClient, onSelectTab }: LargestStacksProps) {
  useCurrency();
  const rows = useMemo(() => stackRows.rows.filter((r) => r.kind !== 'infra'), [stackRows]);
  const stackCount = useMemo(() => rows.reduce((n, r) => n + r.members.length, 0), [rows]);
  const top = rows.slice(0, LARGEST_STACKS);
  const countedPerDay = top.some((r) => r.conversationsApprox);
  // The order is by spend once every row has its spend. Until then a ranking would only move.
  const ranked = stackRows.sortedBy === 'spend';

  const openOnKey = (e: KeyboardEvent<HTMLTableRowElement>, client: string) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    onSelectClient(client);
  };

  let subtitle = `The ${fmtN(top.length)} with the highest total spend, of ${plural(stackCount, 'stack')}. Select a row to open the stack.`;
  if (!ranked && spendFailed) {
    subtitle = `Total spend did not load, so the ${fmtN(top.length)} with the highest model spend are listed, of ${plural(stackCount, 'stack')}.`;
  }
  return (
    <Panel
      title="Largest stacks in this window"
      subtitle={subtitle}
      actions={
        onSelectTab && (
          <Button variant="outline-secondary" size="sm" onClick={() => onSelectTab('clients')}>
            All stacks
          </Button>
        )
      }
      flush
    >
      {rows.length === 0 ? (
        <Empty>This view has no stacks.</Empty>
      ) : !ranked && !spendFailed ? (
        <Empty>
          <Waiting /> Loading spend by stack
        </Empty>
      ) : (
        <div className="dash-scroll dash-scroll--free">
          <Table hover className="dash-table">
            <thead>
              <tr>
                <th>Stack</th>
                <th className="text-end">Total spend</th>
                <th className="text-end">Conversations</th>
                <th className="text-end">Active people</th>
              </tr>
            </thead>
            <tbody>
              {top.map((r) => {
                // A shared AWS account is one row. It opens no one stack: each stack on it has its own link.
                const shared = r.members.length > 1;
                const more = r.members.length - MEMBERS_NAMED;
                return (
                  <tr
                    key={r.client}
                    className={shared ? undefined : 'dash-row'}
                    role={shared ? undefined : 'button'}
                    tabIndex={shared ? undefined : 0}
                    onClick={shared ? undefined : () => onSelectClient(r.client)}
                    onKeyDown={shared ? undefined : (e) => openOnKey(e, r.client)}
                  >
                    <td className="dash-wrap">
                      <span className="dash-name">{r.label}</span>
                      {r.failed ? (
                        <Badge
                          bg="danger"
                          className="dash-ov-stacks__mark"
                          title="The last nightly update failed. These numbers are from the last one that worked."
                        >
                          Update failed
                        </Badge>
                      ) : (
                        r.stale && (
                          <Badge
                            bg="warning"
                            className="dash-ov-stacks__mark"
                            title={`Last updated ${timeAgo(r.generatedAt)}`}
                          >
                            Out of date
                          </Badge>
                        )
                      )}
                      {shared && (
                        <div className="dash-sub dash-ov-stacks__members">
                          One AWS account with {plural(r.members.length, 'stack')}:{' '}
                          {r.members.slice(0, MEMBERS_NAMED).map((member, i) => (
                            <span key={member}>
                              {i > 0 && ', '}
                              <LinkButton onClick={() => onSelectClient(member)} title={`Open ${member}`}>
                                {member}
                              </LinkButton>
                            </span>
                          ))}
                          {more > 0 && ` and ${fmtN(more)} more`}
                        </div>
                      )}
                    </td>
                    <td className="text-end numeric">{r.spend === null ? <Waiting /> : fmtUSD(r.spend)}</td>
                    <td className="text-end numeric">
                      {r.conversationsApprox ? (
                        <span title={COUNTED_PER_DAY}>~{fmtN(r.conversations)}</span>
                      ) : (
                        fmtN(r.conversations)
                      )}
                    </td>
                    <td className="text-end numeric">
                      {r.activeUsers !== null ? fmtN(r.activeUsers) : peopleFailed ? 'n/a' : <Waiting />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
      {(ranked || spendFailed) && countedPerDay && <p className="dash-note dash-note--strip">~ {COUNTED_PER_DAY}</p>}
    </Panel>
  );
}

// ─── one stack: its facts ────────────────────────────────────────────────

const ORGANISATION: Record<NonNullable<StackRow['org']>, string> = {
  nextgen: 'NextGen',
  arcanum: 'Arcanum',
  standalone: 'Standalone: the customer pays its own AWS bill',
};

function AboutStack({ row, data }: { row: StackRow; data: DashboardView }) {
  const counts = useMemo(() => selectCounts(data), [data]);
  const facts: Array<{ label: string; value: ReactNode; detail?: string }> = [];

  if (row.region) facts.push({ label: 'Region', value: row.region });
  if (row.org) facts.push({ label: 'Organisation', value: ORGANISATION[row.org] });
  if (row.kind !== 'infra') {
    facts.push({
      label: 'Contract',
      value: row.contract ?? 'None set',
      detail:
        row.collectible === 'yes'
          ? 'Billed for credits used past its allocation'
          : 'Not billed for credits used past its allocation',
    });
  }
  facts.push({ label: 'Provisioned people', value: fmtN(row.provisionedUsers) });
  facts.push({
    label: 'Agents',
    value: fmtN(counts.agents.value),
    detail: `${fmtN(counts.workspaceAgents.value)} workspace, ${fmtN(counts.personalAgents.value)} personal`,
  });
  facts.push({
    label: 'Active schedules',
    value: fmtN(row.activeSchedules),
    detail: `of ${plural(row.schedules, 'schedule')}`,
  });
  if (row.apps !== null) facts.push({ label: 'Apps and dashboards published', value: fmtN(row.apps) });
  if (row.appBuilder !== null) facts.push({ label: 'App Builder', value: row.appBuilder ? 'On' : 'Off' });
  if (row.sharesAccountWith.length > 0) {
    facts.push({
      label: 'AWS account',
      value: `Shared with ${row.sharesAccountWith.join(', ')}`,
      detail: 'AWS bills by account, so total spend here holds the whole account.',
    });
  }

  return (
    <Panel title="About this stack" scope="now">
      <dl className="dash-ov-facts">
        {facts.map((f) => (
          <div key={f.label} className="dash-ov-facts__item">
            <dt>{f.label}</dt>
            <dd>
              {f.value}
              {f.detail && <span className="dash-ov-facts__detail">{f.detail}</span>}
            </dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}
